import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";

import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";

import { db } from "../firebase";

export default function JoinDeliveryPerson() {
  const navigate = useNavigate();
  const { user, userProfile, loading: authLoading } = useAuth();

  const storage = getStorage();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [application, setApplication] = useState(null);

  /* -----------------------------------------
     DOCUMENT FILES
  ----------------------------------------- */

  const [documents, setDocuments] = useState({
    drivingLicenseFront: null,
    drivingLicenseBack: null,
    aadhaarFront: null,
    aadhaarBack: null,
  });

  /* -----------------------------------------
     EXISTING DOCUMENT URLS
  ----------------------------------------- */

  const [documentUrls, setDocumentUrls] = useState({
    drivingLicenseFront: "",
    drivingLicenseBack: "",
    aadhaarFront: "",
    aadhaarBack: "",
  });

  /* -----------------------------------------
     DOCUMENT PREVIEWS
  ----------------------------------------- */

  const [previews, setPreviews] = useState({
    drivingLicenseFront: "",
    drivingLicenseBack: "",
    aadhaarFront: "",
    aadhaarBack: "",
  });

  const profilePhoto =
    userProfile?.photoURL ||
    user?.photoURL ||
    application?.photo ||
    "";

  const [form, setForm] = useState({
    name: "",
    mobile: "",
    address: "",
    area: "",
    vehicleType: "Bike",
    vehicleNumber: "",
  });

  /* -----------------------------------------
     AUTH + EXISTING APPLICATION
  ----------------------------------------- */

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      sessionStorage.setItem(
        "redirectAfterLogin",
        "/join-delivery"
      );

      navigate("/login");
      return;
    }

    loadApplication();
  }, [user, authLoading, navigate]);

  /* -----------------------------------------
     LOAD APPLICATION
  ----------------------------------------- */

  const loadApplication = async () => {
    if (!user) return;

    try {
      setLoading(true);

      const applicationRef = doc(
        db,
        "deliveryPersons",
        user.uid
      );

      const snap = await getDoc(applicationRef);

      let existingApplication = null;

      if (snap.exists()) {
        existingApplication = {
          id: snap.id,
          ...snap.data(),
        };

        setApplication(existingApplication);

        /* Load existing document URLs */

        setDocumentUrls({
          drivingLicenseFront:
            existingApplication.drivingLicenseFront || "",

          drivingLicenseBack:
            existingApplication.drivingLicenseBack || "",

          aadhaarFront:
            existingApplication.aadhaarFront || "",

          aadhaarBack:
            existingApplication.aadhaarBack || "",
        });
      }

      /*
       * IMPORTANT:
       *
       * Personal information comes from
       * the user's Eyal profile.
       *
       * Vehicle details come from
       * deliveryPersons.
       */

      setForm({
        name:
          userProfile?.name ||
          user.displayName ||
          "",

        mobile:
          userProfile?.mobile ||
          user.phoneNumber ||
          "",

        address:
          userProfile?.address ||
          "",

        area:
          userProfile?.area ||
          "",

        vehicleType:
          existingApplication?.vehicleType ||
          "Bike",

        vehicleNumber:
          existingApplication?.vehicleNumber ||
          "",
      });
    } catch (error) {
      console.error(
        "Failed to load delivery application:",
        error
      );
    } finally {
      setLoading(false);
    }
  };

  /* -----------------------------------------
     INPUT HANDLER
  ----------------------------------------- */

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /* -----------------------------------------
     DOCUMENT FILE HANDLER
  ----------------------------------------- */

  const handleDocumentChange = (e, documentType) => {
    const file = e.target.files?.[0];

    if (!file) return;

    /*
     * Only image files
     */

    if (!file.type.startsWith("image/")) {
      alert("Please select an image file.");
      e.target.value = "";
      return;
    }

    /*
     * Maximum file size = 5 MB
     */

    const maxSize = 5 * 1024 * 1024;

    if (file.size > maxSize) {
      alert("Image size must be 5 MB or less.");
      e.target.value = "";
      return;
    }

    /*
     * Remove previous preview
     */

    if (previews[documentType]) {
      URL.revokeObjectURL(previews[documentType]);
    }

    const previewUrl = URL.createObjectURL(file);

    setDocuments((prev) => ({
      ...prev,
      [documentType]: file,
    }));

    setPreviews((prev) => ({
      ...prev,
      [documentType]: previewUrl,
    }));
  };

  /* -----------------------------------------
     VALIDATE DOCUMENTS
  ----------------------------------------- */

  const validateDocuments = () => {
    if (
      !documents.drivingLicenseFront &&
      !documentUrls.drivingLicenseFront
    ) {
      alert(
        "Please upload the Driving Licence Front."
      );
      return false;
    }

    if (
      !documents.drivingLicenseBack &&
      !documentUrls.drivingLicenseBack
    ) {
      alert(
        "Please upload the Driving Licence Back."
      );
      return false;
    }

    if (
      !documents.aadhaarFront &&
      !documentUrls.aadhaarFront
    ) {
      alert(
        "Please upload the Aadhaar Front."
      );
      return false;
    }

    if (
      !documents.aadhaarBack &&
      !documentUrls.aadhaarBack
    ) {
      alert(
        "Please upload the Aadhaar Back."
      );
      return false;
    }

    return true;
  };

  /* -----------------------------------------
     UPLOAD DOCUMENT TO FIREBASE STORAGE
  ----------------------------------------- */

  const uploadDocument = async (
    file,
    documentType
  ) => {
    /*
     * If user did not select a new file,
     * keep the existing URL.
     */

    if (!file) {
      return documentUrls[documentType] || "";
    }

    const extension =
      file.name.split(".").pop() || "jpg";

    const storagePath =
      `deliveryPersons/${user.uid}/documents/${documentType}.${extension}`;

    const storageRef = ref(
      storage,
      storagePath
    );

    await uploadBytes(
      storageRef,
      file,
      {
        contentType: file.type,
      }
    );

    const downloadUrl =
      await getDownloadURL(storageRef);

    return downloadUrl;
  };

  /* -----------------------------------------
     SUBMIT APPLICATION
  ----------------------------------------- */

  const submitApplication = async (e) => {
    e.preventDefault();

    if (saving) return;

    if (!form.name.trim()) {
      alert("Please enter your name.");
      return;
    }

    if (!form.mobile.trim()) {
      alert("Please enter your mobile number.");
      return;
    }

    if (!form.address.trim()) {
      alert("Please enter your address.");
      return;
    }

    if (!form.area.trim()) {
      alert("Please enter your service area.");
      return;
    }

    if (!form.vehicleNumber.trim()) {
      alert("Please enter your vehicle number.");
      return;
    }

    /*
     * Validate all four documents
     */

    if (!validateDocuments()) {
      return;
    }

    try {
      setSaving(true);

      /*
       * Upload all four documents.
       */

      const [
        drivingLicenseFrontUrl,
        drivingLicenseBackUrl,
        aadhaarFrontUrl,
        aadhaarBackUrl,
      ] = await Promise.all([
        uploadDocument(
          documents.drivingLicenseFront,
          "drivingLicenseFront"
        ),

        uploadDocument(
          documents.drivingLicenseBack,
          "drivingLicenseBack"
        ),

        uploadDocument(
          documents.aadhaarFront,
          "aadhaarFront"
        ),

        uploadDocument(
          documents.aadhaarBack,
          "aadhaarBack"
        ),
      ]);

      const deliveryRef = doc(
        db,
        "deliveryPersons",
        user.uid
      );

      /*
       * Save application.
       */

      await setDoc(
        deliveryRef,
        {
          uid: user.uid,

          name:
            userProfile?.name ||
            user.displayName ||
            "",

          mobile:
            userProfile?.mobile ||
            user.phoneNumber ||
            "",

          address:
            userProfile?.address ||
            "",

          area:
            userProfile?.area ||
            "",

          vehicleType:
            form.vehicleType,

          vehicleNumber:
            form.vehicleNumber
              .trim()
              .toUpperCase(),

          photo:
            user.photoURL ||
            userProfile?.photoURL ||
            "",

          /* ---------------------------------
             IDENTITY DOCUMENTS
          --------------------------------- */

          drivingLicenseFront:
            drivingLicenseFrontUrl,

          drivingLicenseBack:
            drivingLicenseBackUrl,

          aadhaarFront:
            aadhaarFrontUrl,

          aadhaarBack:
            aadhaarBackUrl,

          /* ---------------------------------
             ADMIN APPROVAL
          --------------------------------- */

          verified: false,

          applicationStatus:
            "PENDING",

          /* ---------------------------------
             AVAILABILITY
          --------------------------------- */

          available: false,

          lastCheckIn: null,

          availabilityExpiresAt: null,

          /* ---------------------------------
             DELIVERY STATE
          --------------------------------- */

          currentOrderId: "",

          /* ---------------------------------
             STATISTICS
          --------------------------------- */

          rating: 0,

          totalDeliveries: 0,

          /* ---------------------------------
             TIMESTAMPS
          --------------------------------- */

          createdAt:
            application?.createdAt ||
            serverTimestamp(),

          updatedAt:
            serverTimestamp(),
        },
        {
          merge: true,
        }
      );

      /*
       * Update local state.
       */

      setApplication({
        ...(application || {}),
        uid: user.uid,
        applicationStatus: "PENDING",

        drivingLicenseFront:
          drivingLicenseFrontUrl,

        drivingLicenseBack:
          drivingLicenseBackUrl,

        aadhaarFront:
          aadhaarFrontUrl,

        aadhaarBack:
          aadhaarBackUrl,
      });

      setDocumentUrls({
        drivingLicenseFront:
          drivingLicenseFrontUrl,

        drivingLicenseBack:
          drivingLicenseBackUrl,

        aadhaarFront:
          aadhaarFrontUrl,

        aadhaarBack:
          aadhaarBackUrl,
      });

      alert(
        "✅ Your delivery person application has been submitted.\n\n" +
        "Admin will review your application."
      );

      navigate("/delivery-status");
    } catch (error) {
      console.error(
        "Delivery person application error:",
        error
      );

      alert(
        "Failed to submit application.\n\n" +
        "Please check your internet connection and try again."
      );
    } finally {
      setSaving(false);
    }
  };

  /* -----------------------------------------
     DOCUMENT UPLOAD COMPONENT
  ----------------------------------------- */

  const DocumentUpload = ({
    title,
    documentType,
    existingUrl,
  }) => {
    const preview =
      previews[documentType] ||
      existingUrl;

    return (
      <div style={styles.documentBox}>
        <div style={styles.documentTitle}>
          {title}

          <span style={styles.required}>
            *
          </span>
        </div>

        {preview ? (
          <div style={styles.previewWrapper}>
            <img
              src={preview}
              alt={title}
              style={styles.documentPreview}
            />
          </div>
        ) : (
          <div style={styles.emptyPreview}>
            <span style={styles.uploadIcon}>
              📄
            </span>

            <span>
              No document selected
            </span>
          </div>
        )}

        <label style={styles.uploadButton}>
          {documents[documentType]
            ? "Change Image"
            : existingUrl
            ? "Replace Image"
            : "Choose Image"}

          <input
            type="file"
            accept="image/*"
            onChange={(e) =>
              handleDocumentChange(
                e,
                documentType
              )
            }
            style={{
              display: "none",
            }}
          />
        </label>

        {documents[documentType] && (
          <p style={styles.fileName}>
            {documents[documentType].name}
          </p>
        )}
      </div>
    );
  };

  /* -----------------------------------------
     LOADING
  ----------------------------------------- */

  if (authLoading || loading) {
    return (
      <div style={styles.loading}>
        <h3>Loading...</h3>
      </div>
    );
  }

  /* -----------------------------------------
     EXISTING APPLICATION
  ----------------------------------------- */

  if (application) {
    const status =
      application.applicationStatus ||
      "PENDING";

    /* ---------------------------------------
       PENDING
    --------------------------------------- */

    if (status === "PENDING") {
      return (
        <div style={styles.page}>
          <div style={styles.card}>

            <div
              style={
                styles.profilePhotoWrapper
              }
            >
              {profilePhoto ? (
                <img
                  src={profilePhoto}
                  alt="Profile"
                  style={styles.profilePhoto}
                />
              ) : (
                <div style={styles.icon}>
                  ⏳
                </div>
              )}
            </div>

            <h2 style={styles.title}>
              Application Under Review
            </h2>

            <p style={styles.text}>
              Your delivery person application
              has already been submitted.
            </p>

            <div style={styles.statusBox}>
              <strong>
                Application Status
              </strong>

              <span style={styles.pending}>
                PENDING
              </span>
            </div>

            <p style={styles.smallText}>
              Once an admin approves your
              application, you can start
              checking in as available for
              deliveries.
            </p>

            <button
              style={styles.secondaryBtn}
              onClick={() =>
                navigate("/home")
              }
            >
              Back to Eyal
            </button>
          </div>
        </div>
      );
    }

    /* ---------------------------------------
       APPROVED
    --------------------------------------- */

    if (status === "APPROVED") {
      return (
        <div style={styles.page}>
          <div style={styles.card}>

            <div
              style={
                styles.profilePhotoWrapper
              }
            >
              {profilePhoto ? (
                <img
                  src={profilePhoto}
                  alt="Profile"
                  style={styles.profilePhoto}
                />
              ) : (
                <div style={styles.icon}>
                  🎉
                </div>
              )}
            </div>

            <h2 style={styles.title}>
              You're Approved!
            </h2>

            <p style={styles.text}>
              Your delivery person account
              is active.
            </p>

            <button
              style={styles.btn}
              onClick={() =>
                navigate(
                  "/delivery-dashboard"
                )
              }
            >
              Go to Delivery Dashboard
            </button>
          </div>
        </div>
      );
    }

    /* ---------------------------------------
       REJECTED
    --------------------------------------- */

    if (status === "REJECTED") {
      return (
        <div style={styles.page}>
          <div style={styles.card}>

            <div style={styles.icon}>
              ❌
            </div>

            <h2 style={styles.title}>
              Application Rejected
            </h2>

            <p style={styles.text}>
              Your previous application
              was rejected.
            </p>

            <button
              style={styles.btn}
              onClick={() => {
                setApplication(null);

                /*
                 * Clear selected files.
                 * Existing document URLs remain
                 * available and can be replaced.
                 */

                setDocuments({
                  drivingLicenseFront: null,
                  drivingLicenseBack: null,
                  aadhaarFront: null,
                  aadhaarBack: null,
                });

                setPreviews({
                  drivingLicenseFront: "",
                  drivingLicenseBack: "",
                  aadhaarFront: "",
                  aadhaarBack: "",
                });
              }}
            >
              Apply Again
            </button>
          </div>
        </div>
      );
    }
  }

  /* -----------------------------------------
     REGISTRATION FORM
  ----------------------------------------- */

  return (
    <div style={styles.page}>
      <div style={styles.card}>

        {/* PROFILE PHOTO */}

        <div
          style={
            styles.profilePhotoWrapper
          }
        >
          {profilePhoto ? (
            <img
              src={profilePhoto}
              alt="Profile"
              style={styles.profilePhoto}
            />
          ) : (
            <div style={styles.headerIcon}>
              🚚
            </div>
          )}
        </div>

        <h2 style={styles.title}>
          Become a Delivery Person
        </h2>

        <p style={styles.text}>
          Deliver orders through Eyal and
          earn for every completed delivery.
        </p>

        <form onSubmit={submitApplication}>

          {/* =================================
              PERSONAL DETAILS
          ================================= */}

          <label style={styles.label}>
            Full Name
          </label>

          <input
            type="text"
            name="name"
            value={form.name}
            readOnly
            style={{
              ...styles.input,
              background: "#f3f4f6",
              color: "#555",
              cursor: "not-allowed",
            }}
          />

          <label style={styles.label}>
            Mobile Number
          </label>

          <input
            type="text"
            name="mobile"
            value={form.mobile}
            readOnly
            style={{
              ...styles.input,
              background: "#f3f4f6",
              color: "#555",
              cursor: "not-allowed",
            }}
          />

          <label style={styles.label}>
            Address
          </label>

          <textarea
            name="address"
            value={form.address}
            readOnly
            rows={3}
            style={{
              ...styles.input,
              background: "#f3f4f6",
              color: "#555",
              cursor: "not-allowed",
              resize: "none",
            }}
          />

          <label style={styles.label}>
            Service Area
          </label>

          <input
            type="text"
            name="area"
            value={form.area}
            readOnly
            style={{
              ...styles.input,
              background: "#f3f4f6",
              color: "#555",
              cursor: "not-allowed",
            }}
          />

          {/* =================================
              VEHICLE DETAILS
          ================================= */}

          <div style={styles.sectionHeader}>
            🚗 Vehicle Details
          </div>

          <label style={styles.label}>
            Vehicle Type
          </label>

          <select
            name="vehicleType"
            value={form.vehicleType}
            onChange={handleChange}
            style={styles.input}
          >
            <option value="Bike">
              Bike
            </option>

            <option value="Scooter">
              Scooter
            </option>

            <option value="Auto">
              Auto
            </option>

            <option value="Car">
              Car
            </option>

            <option value="Van">
              Van
            </option>

            <option value="Other">
              Other
            </option>
          </select>

          <label style={styles.label}>
            Vehicle Number
          </label>

          <input
            type="text"
            name="vehicleNumber"
            value={form.vehicleNumber}
            onChange={handleChange}
            placeholder="TN34XX1234"
            style={styles.input}
            autoCapitalize="characters"
          />

          {/* =================================
              IDENTITY DOCUMENTS
          ================================= */}

          <div style={styles.sectionHeader}>
            🪪 Identity Documents
          </div>

          <p style={styles.documentNote}>
            Please upload clear photos of your
            Driving Licence and Aadhaar card.
            All four images are required.
          </p>

          <DocumentUpload
            title="Driving Licence — Front"
            documentType="drivingLicenseFront"
            existingUrl={
              documentUrls.drivingLicenseFront
            }
          />

          <DocumentUpload
            title="Driving Licence — Back"
            documentType="drivingLicenseBack"
            existingUrl={
              documentUrls.drivingLicenseBack
            }
          />

          <DocumentUpload
            title="Aadhaar — Front"
            documentType="aadhaarFront"
            existingUrl={
              documentUrls.aadhaarFront
            }
          />

          <DocumentUpload
            title="Aadhaar — Back"
            documentType="aadhaarBack"
            existingUrl={
              documentUrls.aadhaarBack
            }
          />

          {/* =================================
              SUBMIT
          ================================= */}

          <button
            type="submit"
            disabled={saving}
            style={{
              ...styles.btn,
              opacity: saving ? 0.7 : 1,
              cursor: saving
                ? "not-allowed"
                : "pointer",
            }}
          >
            {saving
              ? "Uploading & Submitting..."
              : "Submit Application"}
          </button>
        </form>

        <p style={styles.footerText}>
          Your application and identity
          documents will be reviewed by Eyal
          Admin before you can accept
          deliveries.
        </p>

      </div>
    </div>
  );
}

/* =========================================================
   STYLES
========================================================= */

const styles = {
  page: {
    minHeight: "100dvh",
    background: "#f9fafb",
    padding: "80px 16px 100px",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
  },

  card: {
    width: "100%",
    maxWidth: 440,
    background: "#ffffff",
    borderRadius: 18,
    padding: 24,
    boxShadow:
      "0 5px 25px rgba(0,0,0,0.08)",
  },

  headerIcon: {
    width: 64,
    height: 64,
    borderRadius: "50%",
    background: "#eff6ff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 32,
    margin: "0 auto 15px",
  },

  icon: {
    fontSize: 45,
    textAlign: "center",
    marginBottom: 10,
  },

  title: {
    textAlign: "center",
    margin: "0 0 8px",
    fontSize: 22,
    color: "#111827",
  },

  text: {
    textAlign: "center",
    color: "#6b7280",
    fontSize: 14,
    lineHeight: 1.5,
    marginBottom: 22,
  },

  label: {
    display: "block",
    fontSize: 13,
    fontWeight: 600,
    color: "#374151",
    marginBottom: 6,
    marginTop: 14,
  },

  input: {
    width: "100%",
    boxSizing: "border-box",
    border: "1px solid #d1d5db",
    borderRadius: 10,
    padding: "11px 12px",
    fontSize: 14,
    outline: "none",
    background: "#fff",
  },

  sectionHeader: {
    fontSize: 16,
    fontWeight: 700,
    color: "#111827",
    marginTop: 28,
    marginBottom: 10,
    paddingBottom: 8,
    borderBottom:
      "1px solid #e5e7eb",
  },

  documentNote: {
    fontSize: 12,
    lineHeight: 1.5,
    color: "#6b7280",
    margin: "0 0 14px",
  },

  documentBox: {
    border: "1px solid #d1d5db",
    borderRadius: 14,
    padding: 14,
    marginTop: 14,
    background: "#fafafa",
  },

  documentTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: "#374151",
    marginBottom: 10,
  },

  required: {
    color: "#dc2626",
    marginLeft: 3,
  },

  previewWrapper: {
    width: "100%",
    height: 180,
    borderRadius: 10,
    overflow: "hidden",
    background: "#f3f4f6",
    marginBottom: 10,
    border: "1px solid #e5e7eb",
  },

  documentPreview: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    display: "block",
  },

  emptyPreview: {
    width: "100%",
    height: 120,
    borderRadius: 10,
    background: "#f3f4f6",
    border: "1px dashed #cbd5e1",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    color: "#9ca3af",
    fontSize: 12,
    marginBottom: 10,
    gap: 6,
  },

  uploadIcon: {
    fontSize: 28,
  },

  uploadButton: {
    display: "flex",
    width: "100%",
    height: 40,
    borderRadius: 9,
    background: "#eff6ff",
    color: "#2563eb",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    boxSizing: "border-box",
  },

  fileName: {
    margin: "8px 0 0",
    fontSize: 11,
    color: "#6b7280",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  btn: {
    width: "100%",
    height: 46,
    border: "none",
    borderRadius: 11,
    background: "#16a34a",
    color: "#fff",
    fontSize: 15,
    fontWeight: 700,
    cursor: "pointer",
    marginTop: 22,
  },

  secondaryBtn: {
    width: "100%",
    height: 44,
    border: "none",
    borderRadius: 11,
    background: "#f3f4f6",
    color: "#374151",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    marginTop: 16,
  },

  statusBox: {
    background: "#fff7ed",
    borderRadius: 12,
    padding: 15,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 20,
  },

  pending: {
    color: "#d97706",
    fontWeight: 700,
    fontSize: 13,
  },

  smallText: {
    textAlign: "center",
    color: "#6b7280",
    fontSize: 13,
    lineHeight: 1.5,
    marginTop: 18,
  },

  footerText: {
    textAlign: "center",
    color: "#9ca3af",
    fontSize: 12,
    lineHeight: 1.5,
    marginTop: 18,
    marginBottom: 0,
  },

  loading: {
    minHeight: "100dvh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    background: "#f9fafb",
  },

  profilePhotoWrapper: {
    width: 76,
    height: 76,
    borderRadius: "50%",
    margin: "0 auto 15px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    background: "#eff6ff",
    border: "3px solid #ffffff",
    boxShadow:
      "0 2px 8px rgba(0,0,0,0.12)",
  },

  profilePhoto: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    borderRadius: "50%",
  },
};
